import { useAppDispatch, useAppSelector } from "../state/hooks"
import { updateAsset, getAssetById } from "../state/slices/assetsSlice"
import { useEffect } from "react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"

/**
 * Hook for fetching and managing individual asset data
 * This replaces the need for AssetProvider for detail views
 *
 * @param assetId - The ID of the asset to fetch
 * @returns Asset data, loading state, and refetch function
 */
export function useAsset(assetId: string | undefined) {
  const dispatch = useAppDispatch()

  // Get asset from Redux store first (might be from paginated list)
  const assetFromStore = useAppSelector((state) => getAssetById(state, assetId))

  // Fetch individual asset if we don't have it or need fresh data
  const { data, loading, error, refetch } = useQueryWithEndpoint<
    gqlTypes.GetAssetQuery,
    gqlTypes.GetAssetQueryVariables
  >(
    gqlTypes.GetAssetDocument,
    {
      variables: { id: assetId || "" },
      skip: !assetId,
      fetchPolicy: "cache-and-network", // Always fetch fresh data from network
    },
    "primary", // Use the primary (experience API) endpoint
  )

  // Update Redux store when we get fresh asset data
  useEffect(() => {
    if (data?.asset) {
      dispatch(updateAsset(data.asset))
    }
  }, [data?.asset, dispatch])

  // Return the most up-to-date asset data
  const asset = data?.asset || assetFromStore

  return {
    asset,
    loading,
    error: error?.message || null,
    refetch,
  }
}

/**
 * Simple hook for just getting loading state (replaces useAssetsLoading)
 * This is for components that just need to know if any asset is loading
 */
export function useAssetLoading(assetId: string | undefined) {
  const { loading } = useAsset(assetId)
  return loading
}
